import type {
  Pill,
  PillOption,
  PillTone,
} from 'features/libra/dashboard/ticketDashboard/types'
import {
  getDefaultStage,
  getEffectiveStageId,
  type LifecycleConfig,
  type LifecycleStage,
} from 'features/libra/lifecycle/lifecycleTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

import { STAGE_CATEGORY } from './categoryIds'

/** Stages for workflows without a lifecycle, matching the old list view's badges. */
const COMPLETE_STAGE: PillOption = {
  value: 'complete',
  label: 'Complete',
  tone: 'success',
}
const DRAFT_STAGE: PillOption = { value: 'draft', label: 'Draft' }

function stageTone(stage: LifecycleStage): PillTone {
  if (stage.color) return { color: stage.color }
  return stage.terminal ? 'success' : 'processing'
}

function toStageOption(stage: LifecycleStage): PillOption {
  return { value: stage.id, label: stage.name, tone: stageTone(stage) }
}

interface ResolvedStage {
  option: PillOption
  /** A final stage, e.g. Approved. */
  terminal: boolean
}

interface StageResolver {
  options: PillOption[]
  resolve: (ticket: LibraTicket) => ResolvedStage
}

function lifecycleResolver(config: LifecycleConfig): StageResolver {
  const options = config.stages.map(toStageOption)
  const byId = new Map(options.map((option) => [option.value, option]))
  const knownStageIds = new Set(byId.keys())
  const defaultStageId = getDefaultStage(config)?.id
  const terminalIds = new Set(
    config.stages.filter((stage) => stage.terminal).map((stage) => stage.id)
  )
  return {
    options,
    resolve: (ticket) => {
      const stageId = getEffectiveStageId(
        ticket.ticketStatus,
        knownStageIds,
        defaultStageId
      )
      return {
        option: (stageId && byId.get(stageId)) || DRAFT_STAGE,
        terminal: stageId !== undefined && terminalIds.has(stageId),
      }
    },
  }
}

const NO_LIFECYCLE_RESOLVER: StageResolver = {
  options: [DRAFT_STAGE, COMPLETE_STAGE],
  resolve: (ticket) => ({
    option: ticket.workflowComplete ? COMPLETE_STAGE : DRAFT_STAGE,
    terminal: ticket.workflowComplete,
  }),
}

/**
 * Resolves every ticket to a known stage: its lifecycle stage, else the
 * default stage. Workflows without a lifecycle use Draft / Complete.
 */
export function createStagePills(workflows: ReadonlyArray<LibraWorkflow>) {
  const resolvers = new Map(
    workflows.map((workflow) => [
      workflow.id,
      workflow.lifecycleConfig
        ? lifecycleResolver(workflow.lifecycleConfig)
        : NO_LIFECYCLE_RESOLVER,
    ])
  )

  const options = new Map<string, PillOption>()
  resolvers.forEach((resolver) =>
    resolver.options.forEach((option) => {
      if (!options.has(option.value)) options.set(option.value, option)
    })
  )

  return {
    /** Every stage across the workflows, in lifecycle order. */
    options: [...options.values()],
    resolve: (ticket: LibraTicket): { pill: Pill; terminal: boolean } => {
      const resolver = resolvers.get(ticket.workflowId) ?? NO_LIFECYCLE_RESOLVER
      const { option, terminal } = resolver.resolve(ticket)
      return { pill: { category: STAGE_CATEGORY, ...option }, terminal }
    },
  }
}

export type StagePills = ReturnType<typeof createStagePills>
